import { Button } from "@/components/primitives/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/primitives/drawer";
import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";

import {
  VolumeOffIcon,
  Volume2Icon,
  Ellipsis,
  LogOut,
  Trash2,
  Info,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteP2PChat from "@/components/shared/dialogs/DeleteP2PChat";
import DeleteGroupChat from "@/components/shared/dialogs/DeleteGroupChat";
import LeaveGroupChat from "@/components/shared/dialogs/LeaveGroupChat";

type MobileChatMenuProps = {
  onMute: () => void;
  onUnMute: () => void;
};

export default function MobileChatMenu({
  onMute,
  onUnMute,
}: MobileChatMenuProps) {
  const { t } = useTranslation();
  const activeChatMuted = useNavigationState((state) => state.activeChatMuted);
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const toggleChatDetails = useNavigationState(
    (state) => state.toggleChatDetails,
  );

  const [openDialog, setOpenDialog] = useState<
    "delete-p2p-chat" | "delete-group-chat" | "leave-group-chat" | null
  >(null);

  return (
    <>
      <Drawer modal={false}>
        <DrawerTrigger className="ml-auto">
          <Button
            asChild
            className="size-5 cursor-pointer"
            size="icon"
            variant="ghost"
          >
            <Ellipsis
              className="shrink-0 border-none outline-none"
              size={20}
              strokeWidth={2}
            />
          </Button>
        </DrawerTrigger>
        <DrawerContent aria-describedby="chat-menu" className="px-4 pb-4">
          <DrawerHeader />
          <DrawerTitle />
          <DrawerDescription />

          {activeChatMuted ? (
            <DrawerClose asChild>
              <Button
                variant="link"
                className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
                onClick={onUnMute}
              >
                <VolumeOffIcon className="size-4" /> {t("unmute")}
              </Button>
            </DrawerClose>
          ) : (
            <DrawerClose asChild>
              <Button
                variant="link"
                className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
                onClick={onMute}
              >
                <Volume2Icon className="size-4" /> {t("mute")}
              </Button>
            </DrawerClose>
          )}

          <DrawerClose asChild>
            <Button
              variant="link"
              className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
              onClick={() => toggleChatDetails()}
            >
              <Info className="size-4" /> {t("chatInfo")}
            </Button>
          </DrawerClose>

          {activeChatType === ChatType.P2P && (
            <DrawerClose asChild>
              <Button
                variant="link"
                className="mb-1 flex items-center justify-start gap-3 text-base text-destructive no-underline hover:no-underline"
                onClick={() => {
                  setOpenDialog("delete-p2p-chat");
                  // handleDeleteP2PChat
                }}
              >
                <Trash2 className="size-4" /> {t("delete")}
              </Button>
            </DrawerClose>
          )}

          {activeChatType === ChatType.GROUP &&
            (activeChatRoleAsMember === MemberRole.OWNER ? (
              <DrawerClose asChild>
                <Button
                  variant="link"
                  className="mb-1 flex items-center justify-start gap-3 text-base text-destructive no-underline hover:no-underline"
                  onClick={() => {
                    setOpenDialog("delete-group-chat");
                    // handleDeleteGroupChat
                  }}
                >
                  <Trash2 className="size-4" /> {t("deleteGroup")}
                </Button>
              </DrawerClose>
            ) : (
              <DrawerClose asChild>
                <Button
                  variant="link"
                  className="mb-1 flex items-center justify-start gap-3 text-base text-destructive no-underline hover:no-underline"
                  onClick={() => {
                    setOpenDialog("leave-group-chat");
                    // handleLeaveChat
                  }}
                >
                  <LogOut className="size-4" /> {t("leave")}
                </Button>
              </DrawerClose>
            ))}
        </DrawerContent>
      </Drawer>

      {/* CONFIRMATION DIALOGS */}

      <DeleteP2PChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-p2p-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-p2p-chat" : null);
        }}
      />

      <DeleteGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />

      <LeaveGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "leave-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />
    </>
  );
}
